import React from 'react';
import { Container } from 'react-bootstrap';
import { Button, Result, Skeleton, Alert, Modal } from 'antd';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import OnError from '../../../../components/OnError';
import NextStepButton from '../../components/NextStepButton';
import PaymentForm from './PaymentForm';
import { useGet as useGetLocalSchedule } from '../../../../hooks/localSchedule/useGet';
import { useClear as useClearLocalSchedule } from '../../../../hooks/localSchedule/useClear';
import { useCancelRegistration } from '../../../../hooks/useCancelRegistration';
import { useGetStudents } from '../../../../hooks/useGetStudents';
import { getPaymentSuccessMessage } from '../../../../utils/subsidy';

const DECIMAL_TOTAL = /^\d+(\.\d+)?$/;

function parseTotal(total: unknown): number | undefined {
  if (typeof total === 'number') {
    return Number.isFinite(total) && total >= 0 ? total : undefined;
  }
  if (typeof total === 'string' && DECIMAL_TOTAL.test(total)) {
    return Number(total);
  }
  return undefined;
}

// Define interface for register props
interface RegisterData {
  data?: {
    total?: number | string;
    paymentIntent?:
      | {
          id?: string;
          client_secret?: string;
          status?: string;
        }
      | string;
    metadata?: {
      requiresPayment?: boolean;
      isBilledToOrganization?: boolean;
    };
    studentId?: number;
    studentRegistrationWindowId?: number;
    studentsSchedules?: Array<{
      currentlySchedulingId?: string;
    }>;
  };
  isLoading: boolean;
  isError: boolean;
  error?: any;
}

interface PayFeesProps {
  register: RegisterData;
  /** Omit once a registration exists; "back" then leaves checkout. */
  onGoBack?: () => void;
  onCancel?: () => void;
  isCanceling?: boolean;
}

// loadStripe injects Stripe.js and must run once per key, not per render.
const stripePromises = new Map<string, Promise<Stripe | null>>();
const getStripe = (publicKey: string) => {
  if (!stripePromises.has(publicKey)) {
    stripePromises.set(publicKey, loadStripe(publicKey));
  }
  return stripePromises.get(publicKey) as Promise<Stripe | null>;
};

/**
 * PayFees component
 * @param register - the result of your registration API call (React Query, etc.)
 * @param onGoBack - function to go back in your flow
 */
export default function PayFees({ register, onGoBack }: PayFeesProps) {
  const isProductionStripe = process.env.REACT_APP_NODE_ENV === 'production';
  const stripePublicKey = isProductionStripe
    ? process.env.REACT_APP_STRIPE_LIVE_PUBLIC
    : process.env.REACT_APP_STRIPE_TEST_PUBLIC;
  const stripePromise = stripePublicKey
    ? getStripe(stripePublicKey)
    : Promise.resolve(null);
  const missingStripeKeyEnvName = isProductionStripe
    ? 'REACT_APP_STRIPE_LIVE_PUBLIC'
    : 'REACT_APP_STRIPE_TEST_PUBLIC';
  const navigate = useNavigate();
  // Checkout has started, so the cart's 15-minute shopping timer no longer
  // applies; the API re-checks availability when registering.
  const localSchedule = useGetLocalSchedule(true);
  const clearLocalSchedule = useClearLocalSchedule();
  const cancelRegistrationMutation = useCancelRegistration();
  const [showCancelModal, setShowCancelModal] = React.useState(false);

  // Fetch students to get subsidy information
  const { data: students } = useGetStudents();

  // An incomplete response is not a zero-payment registration. The API sends
  // fees as DECIMAL strings ("100.00"), so accept those but nothing looser.
  const amount = parseTotal(register.data?.total);
  const hasValidTotal = amount !== undefined;

  // Pending enrollment responses can be recovered without local schedule state,
  // so prefer the studentId returned by the backend when it exists.
  const currentStudentId =
    register.data?.studentId || localSchedule?.selections?.[0]?.studentId;
  const student = students?.find((s: any) => +s.id === +currentStudentId);
  const subsidyOrgName =
    student?.studentsSubsidyEligible?.SubsidyProgram?.programName;
  const requiresPayment = register.data?.metadata?.requiresPayment;

  // The backend sets 'paymentIntent' in 'register.data?.return?.paymentIntent'
  // Safe extraction of client_secret, handling both object and string cases
  const clientSecret =
    typeof register.data?.paymentIntent === 'object' &&
    register.data?.paymentIntent !== null
      ? (register.data?.paymentIntent as any)?.client_secret || ''
      : '';

  const paymentStatus =
    typeof register.data?.paymentIntent === 'object'
      ? register.data.paymentIntent?.status
      : undefined;
  const isPaymentPaid = paymentStatus === 'succeeded';

  const paymentIntentId =
    typeof register.data?.paymentIntent === 'string'
      ? register.data?.paymentIntent
      : register.data?.paymentIntent?.id || '';

  const currentlySchedulingId =
    register.data?.studentsSchedules?.[0]?.currentlySchedulingId || '';
  // The cancellation endpoint accepts only StudentRegistrationWindow IDs.
  const cancelRegistrationId = register.data?.studentRegistrationWindowId;
  const canCancelRegistration =
    Number.isSafeInteger(cancelRegistrationId) &&
    Number(cancelRegistrationId) > 0 &&
    !isPaymentPaid &&
    paymentStatus !== 'processing';
  const cancellationUnavailable =
    'Reload this registration before canceling. Its registration information is incomplete.';

  const handleOnGoBack = () => {
    if (onGoBack) {
      onGoBack();
    } else {
      navigate('/scheduling');
    }
  };

  const handleOnNextStep = () => {
    // Clear localSchedule data on success
    if (
      localSchedule &&
      localSchedule.selections &&
      localSchedule.selections.length > 0
    ) {
      // Completely clear the localSchedule
      clearLocalSchedule();
    }
    window.location.href = `${window.location.origin}/finalize/${currentlySchedulingId}/${paymentIntentId}?redirect_status=succeeded`;
  };

  const handleCancelRegistration = () => {
    if (!canCancelRegistration) {
      Modal.error({
        title: 'Unable to Cancel',
        content: cancellationUnavailable,
      });
      return;
    }

    // Clear local schedule data
    if (
      localSchedule &&
      localSchedule.selections &&
      localSchedule.selections.length > 0
    ) {
      clearLocalSchedule();
    }

    // Use the mutation hook to cancel the registration
    // The hook will handle success/error messaging
    cancelRegistrationMutation.mutate(String(cancelRegistrationId), {
      onSuccess: () => {
        // Override the default behavior to navigate to scheduling instead of refreshing
        navigate('/scheduling');
        setShowCancelModal(false);
      },
      onError: () => {
        setShowCancelModal(false);
      },
    });
  };

  const showCancelConfirmation = () => {
    setShowCancelModal(true);
  };

  const hideCancelConfirmation = () => {
    setShowCancelModal(false);
  };

  // Rendered in every view that offers "Cancel Registration".
  const cancelModal = (
    <Modal
      title="Cancel Registration"
      open={showCancelModal}
      onOk={handleCancelRegistration}
      onCancel={hideCancelConfirmation}
      okText="Yes, Cancel Registration"
      cancelText="Keep Registration"
      okButtonProps={{
        danger: true,
        disabled: !canCancelRegistration,
        loading: cancelRegistrationMutation.isLoading,
      }}
      cancelButtonProps={{
        disabled: cancelRegistrationMutation.isLoading,
      }}
    >
      <p>Are you sure you want to cancel your registration?</p>
      <p>
        <strong>This action cannot be undone.</strong> All your registration
        data including:
      </p>
      <ul>
        <li>Selected schedules and time slots</li>
        <li>Student registration information</li>
        <li>Payment intent (if created)</li>
      </ul>
      <p>will be permanently removed from the system.</p>
    </Modal>
  );

  // --- Loading ---
  if (register.isLoading) {
    return (
      <Container>
        <div role="progressbar">
          <Skeleton active />
        </div>
      </Container>
    );
  }

  // --- Error Handling ---
  if (register.isError) {
    // Instead of using getDataFromApiError directly, we manually extract the error data
    const errorMessage =
      register.error?.response?.data?.message || 'Something went wrong';

    return (
      <Container>
        <OnError
          title="Error"
          status={500}
          subTitle={errorMessage}
          extra={
            <>
              <Button type="default" onClick={handleOnGoBack}>
                Go Back
              </Button>
              {currentlySchedulingId && !isPaymentPaid && (
                <Button
                  type="default"
                  danger
                  onClick={showCancelConfirmation}
                  disabled={!canCancelRegistration}
                  title={
                    !canCancelRegistration ? cancellationUnavailable : undefined
                  }
                  style={{ marginLeft: 8 }}
                >
                  Cancel Registration
                </Button>
              )}
            </>
          }
        />
        {cancelModal}
      </Container>
    );
  }

  // Without registration data we can't tell what is owed. Treating a missing
  // total as $0 would skip payment, e.g. after a failed Stripe redirect.
  if (!register.data) {
    return (
      <Container>
        <OnError
          title="Registration Not Found"
          status={404}
          subTitle="We couldn't load this registration. Please return to scheduling and try again."
          extra={
            <Button type="default" onClick={handleOnGoBack}>
              Go Back
            </Button>
          }
        />
      </Container>
    );
  }

  if (!hasValidTotal) {
    return (
      <Container>
        <OnError
          title="Payment Details Unavailable"
          status={500}
          subTitle="We couldn't determine the amount due. Reload this registration or return to scheduling and try again."
          extra={
            <>
              <Button onClick={handleOnGoBack}>Go Back</Button>
              {currentlySchedulingId && !isPaymentPaid && (
                <Button
                  danger
                  onClick={showCancelConfirmation}
                  disabled={!canCancelRegistration}
                  title={
                    !canCancelRegistration ? cancellationUnavailable : undefined
                  }
                >
                  Cancel Registration
                </Button>
              )}
            </>
          }
        />
        {cancelModal}
      </Container>
    );
  }

  // A bank payment can still be settling when checkout is resumed. It must
  // not be submitted again, canceled, or treated as a succeeded payment.
  if (paymentStatus === 'processing') {
    return (
      <Container>
        <Result
          status="info"
          title="Payment Processing"
          subTitle="Your payment was submitted and is still processing. Return to scheduling to check its status before signing your contracts."
          extra={
            <Button type="primary" onClick={() => navigate('/scheduling')}>
              Return to Scheduling
            </Button>
          }
        />
      </Container>
    );
  }

  // --- No Payment Required ---
  const shouldTreatAsNoPayment =
    requiresPayment === false || (requiresPayment !== true && amount === 0);

  if (shouldTreatAsNoPayment) {
    // Determine the success message based on subsidy status
    const successMessage = student?.studentsSubsidyEligible
      ? getPaymentSuccessMessage(
          student.studentsSubsidyEligible,
          subsidyOrgName
        )
      : 'No payment required';

    return (
      <Container>
        <Result status="success" title="Success" subTitle={successMessage} />
        <NextStepButton disabled={false} onClick={handleOnNextStep} />
        {currentlySchedulingId && !isPaymentPaid && (
          <Button
            block
            type="default"
            danger
            onClick={showCancelConfirmation}
            disabled={!canCancelRegistration}
            title={!canCancelRegistration ? cancellationUnavailable : undefined}
            size="large"
            style={{ marginTop: 16 }}
          >
            Cancel Registration
          </Button>
        )}
        {cancelModal}
      </Container>
    );
  }

  // --- Payment Required ---
  // If the backend returned a valid clientSecret, render the PaymentForm.
  if (!stripePublicKey) {
    return (
      <Container>
        <OnError
          title="Stripe Configuration Error"
          status={500}
          subTitle={`Missing required Stripe publishable key: ${missingStripeKeyEnvName}`}
          extra={
            <Button type="default" onClick={handleOnGoBack}>
              Go Back
            </Button>
          }
        />
      </Container>
    );
  }

  if (!clientSecret) {
    console.error('PaymentIntent details:', {
      paymentIntent: register.data?.paymentIntent,
      isString: typeof register.data?.paymentIntent === 'string',
      isObject: typeof register.data?.paymentIntent === 'object',
      hasClientSecret:
        typeof register.data?.paymentIntent === 'object' &&
        register.data?.paymentIntent !== null &&
        'client_secret' in register.data?.paymentIntent,
      clientSecretValue:
        typeof register.data?.paymentIntent === 'object' &&
        register.data?.paymentIntent !== null
          ? (register.data?.paymentIntent as any)?.client_secret
          : 'N/A',
      amount: amount,
      total: register.data?.total,
    });

    return (
      <Container>
        <Alert
          message="Missing client secret"
          description={
            <>
              <p>We could not retrieve the Stripe client secret.</p>
              <p>PaymentIntent ID: {paymentIntentId || 'Not available'}</p>
            </>
          }
          type="error"
          showIcon
        />
        <Button onClick={handleOnGoBack} style={{ marginTop: 16 }}>
          Go Back
        </Button>
        {currentlySchedulingId && !isPaymentPaid && (
          <Button
            block
            type="default"
            danger
            onClick={showCancelConfirmation}
            disabled={!canCancelRegistration}
            title={!canCancelRegistration ? cancellationUnavailable : undefined}
            style={{ marginTop: 16, marginLeft: 8 }}
          >
            Cancel Registration
          </Button>
        )}
        {cancelModal}
      </Container>
    );
  }

  // We re-mount Elements with the clientSecret (necessary for PaymentElement usage).
  const appearance = { theme: 'stripe' as const };
  const options = {
    clientSecret,
    appearance,
  };

  return (
    <Container>
      <Elements stripe={stripePromise} options={options}>
        <PaymentForm
          paymentIntentId={paymentIntentId}
          currentlySchedulingId={currentlySchedulingId}
          amount={amount}
        />
      </Elements>

      {/* Cancel Registration Button */}
      {currentlySchedulingId && !isPaymentPaid && (
        <div
          style={{
            textAlign: 'center',
            marginTop: 24,
            paddingTop: 8,
          }}
        >
          <Button
            block
            type="default"
            danger
            onClick={showCancelConfirmation}
            disabled={!canCancelRegistration}
            title={!canCancelRegistration ? cancellationUnavailable : undefined}
            size="large"
          >
            Cancel Registration
          </Button>
        </div>
      )}

      {cancelModal}
    </Container>
  );
}
